import { useEffect, useRef, useState } from 'preact/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge, Banner, Button, Card, EmptyState } from '../components/ui'
import {
  applyProposal,
  getKnowledge,
  getMe,
  getPlaybook,
  tuneApply,
  tuneChat,
  type TuneDraft,
} from '../api/endpoints'
import { siteMeta } from '../utils/site'
import type { Playbook, SiteId } from '../types'

/** Mirrors the worker's 11-field playbook schema + the knowledge doc. */
const TARGET_LABEL: Record<string, string> = {
  voice: 'Voice & tone',
  coreOffer: 'Core offer',
  notInScope: 'What we do NOT do',
  pricing: 'Pricing posture',
  disqualifiers: 'Disqualifying signals',
  commonConfusions: 'Common visitor confusions',
  alwaysEscalate: 'Always escalate',
  mustNeverPromise: 'Must never promise',
  proofPoints: 'Proof points',
  examples: 'Example exchanges',
  notes: 'Notes',
  knowledge: 'Knowledge doc',
}

interface ChatTurn {
  role: 'user' | 'assistant'
  content: string
  drafts?: TuneDraft[]
}

const STARTERS = [
  'Work through the open proposals with me.',
  'The bot got something wrong — help me fix it.',
  'I want to add a new fact the bot should know.',
]

/**
 * Tune — the conversational improvement copilot (C2). Discusses the bot's
 * open quality proposals (and ad-hoc requests), drafts exact playbook /
 * knowledge edits, and surfaces each draft as a card with the current value
 * and an Approve button. Nothing is written without approval; every apply is
 * audited and revertible from the Quality page.
 */
export function TunePage() {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe })
  const sites: SiteId[] = meQuery.data?.sites ?? []
  const [site, setSite] = useState<SiteId | ''>('')
  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [input, setInput] = useState('')
  const [conversationId, setConversationId] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const deepLinkRef = useRef(false)

  useEffect(() => {
    if (sites.length === 1) setSite(sites[0])
  }, [sites])

  // Deep link from the conversation detail page: /tune?site=…&conversation=…
  // loads that conversation's transcript into Tune's context and opens with
  // the "bot got something wrong" starter, ready to discuss the fix.
  useEffect(() => {
    if (deepLinkRef.current || sites.length === 0) return
    const params = new URLSearchParams(window.location.search)
    const conv = params.get('conversation')
    const s = params.get('site')
    if (conv && s && sites.includes(s as SiteId)) {
      deepLinkRef.current = true
      setSite(s as SiteId)
      setConversationId(conv)
    }
  }, [sites])

  const send = useMutation({
    mutationFn: (userText: string) => {
      const history = [...turns, { role: 'user' as const, content: userText }]
      return tuneChat(
        site as SiteId,
        history.map(({ role, content }) => ({ role, content })),
        conversationId ?? undefined
      )
    },
    onMutate: (userText: string) => {
      setTurns((t) => [...t, { role: 'user', content: userText }])
      setInput('')
    },
    onSuccess: (data) => {
      setTurns((t) => [...t, { role: 'assistant', content: data.reply, drafts: data.drafts }])
    },
  })

  // Auto-open the deep-linked discussion once site + conversation are set.
  useEffect(() => {
    if (conversationId && site && turns.length === 0 && !send.isPending) {
      send.mutate('The bot got something wrong in this conversation — help me fix it.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, site])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [turns, send.isPending])

  const submit = () => {
    const text = input.trim()
    if (!text || !site || send.isPending) return
    send.mutate(text)
  }

  if (meQuery.isLoading) return <div style={{ padding: 24 }}>Loading…</div>
  if (sites.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <EmptyState title="No sites assigned" subtitle="Ask an admin to grant access to a site." />
      </div>
    )
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Tune</h1>
        <span style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)' }}>
          discuss improvements — approved drafts go live instantly, revert from Quality
        </span>
        {conversationId && (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 10px', borderRadius: 999, background: 'var(--m-bg-subtle, #eef2ff)', border: '1px solid var(--m-border, #d1d5db)', color: 'var(--m-text, #374151)' }}
            title={`Tune has this conversation's transcript in context (${conversationId})`}
          >
            ⚙ tuning from a conversation
            <button
              onClick={() => setConversationId(null)}
              title="Detach the conversation transcript"
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1, color: 'inherit' }}
            >
              ✕
            </button>
          </span>
        )}
        <select
          value={site}
          onChange={(e) => {
            setSite((e.target as HTMLSelectElement).value as SiteId)
            setTurns([])
            setConversationId(null)
          }}
          style={{ marginLeft: 'auto', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--m-border, #d1d5db)', fontSize: 14, background: 'var(--m-surface, #fff)', color: 'var(--m-text, #111827)' }}
        >
          {!site && <option value="">Choose a site…</option>}
          {sites.map((s) => <option key={s} value={s}>{siteMeta(s).name}</option>)}
        </select>
      </div>

      <Card style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)', minHeight: 380 }}>
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, paddingRight: 4 }}>
          {turns.length === 0 && (
            <div style={{ margin: 'auto', textAlign: 'center', maxWidth: 420 }}>
              <div style={{ fontSize: 14, color: 'var(--m-text-muted, #6b7280)', marginBottom: 12 }}>
                {site
                  ? 'Tune knows this site’s playbook, knowledge doc, and open quality proposals. Start with:'
                  : 'Choose a site to start.'}
              </div>
              {site && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {STARTERS.map((s) => (
                    <Button key={s} variant="outline" onClick={() => send.mutate(s)}>{s}</Button>
                  ))}
                </div>
              )}
            </div>
          )}
          {turns.map((t, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div
                style={{
                  alignSelf: t.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  padding: '8px 12px',
                  borderRadius: 12,
                  fontSize: 14,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  background: t.role === 'user' ? 'var(--m-primary, #4f46e5)' : 'var(--m-bg-subtle, #f3f4f6)',
                  color: t.role === 'user' ? '#fff' : 'var(--m-text, #111827)',
                }}
              >
                {t.content || (t.drafts?.length ? '(drafted a change below)' : '')}
              </div>
              {t.drafts?.map((d, j) => (
                <DraftCard key={`${i}-${j}`} site={site as SiteId} draft={d} />
              ))}
            </div>
          ))}
          {send.isPending && (
            <div style={{ alignSelf: 'flex-start', fontSize: 13, color: 'var(--m-text-muted, #6b7280)', padding: '6px 12px' }}>
              Tune is thinking…
            </div>
          )}
          {send.isError && (
            <Banner variant="error">{(send.error as Error)?.message ?? "Couldn't reach Tune."}</Banner>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: '1px solid var(--m-border, #e5e7eb)', marginTop: 10 }}>
          <textarea
            value={input}
            onInput={(e) => setInput((e.target as HTMLTextAreaElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                submit()
              }
            }}
            rows={1}
            placeholder={site ? 'Message Tune… (Enter to send)' : 'Choose a site first'}
            disabled={!site || send.isPending}
            style={{ flex: 1, resize: 'none', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--m-border, #d1d5db)', fontSize: 14, fontFamily: 'inherit', lineHeight: 1.4 }}
          />
          <Button onClick={submit} disabled={!site || !input.trim()} loading={send.isPending}>Send</Button>
        </div>
      </Card>
    </>
  )
}

/**
 * One drafted edit: target, editable text, the current value it lands in,
 * and the approval gate. Applies via the audited proposal path — drafts tied
 * to an open proposal use its id; ad-hoc drafts get a 'tune' proposal row.
 */
function DraftCard({ site, draft }: { site: SiteId; draft: TuneDraft }) {
  const queryClient = useQueryClient()
  const [text, setText] = useState(draft.text)
  const isKnowledge = draft.targetField === 'knowledge'

  const pbQuery = useQuery({
    queryKey: ['playbook', site],
    queryFn: () => getPlaybook(site),
    enabled: !isKnowledge,
  })
  const kQuery = useQuery({
    queryKey: ['knowledge', site],
    queryFn: () => getKnowledge(site),
    enabled: isKnowledge,
  })
  const current = isKnowledge
    ? (kQuery.data?.knowledgeText ?? '')
    : ((pbQuery.data?.playbook as Playbook | undefined)?.[draft.targetField as keyof Playbook] ?? '')
  const TAIL = 400
  const currentTail = current.length > TAIL ? '…' + current.slice(-TAIL) : current

  const apply = useMutation({
    mutationFn: () => {
      const input = { targetField: draft.targetField, mode: draft.mode, text: text.trim() }
      return draft.proposalId
        ? applyProposal(draft.proposalId, input)
        : tuneApply({ site, ...input, summary: draft.rationale || `Tune edit to ${draft.targetField}` })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      queryClient.invalidateQueries({ queryKey: ['proposals-applied'] })
      queryClient.invalidateQueries({ queryKey: ['playbook', site] })
      queryClient.invalidateQueries({ queryKey: ['knowledge', site] })
    },
  })

  const applied = apply.isSuccess

  return (
    <div style={{ alignSelf: 'stretch', maxWidth: 640, border: '1px solid var(--m-border, #d1d5db)', borderRadius: 10, padding: 12, background: 'var(--m-surface, #fff)', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <Badge variant="neutral">{TARGET_LABEL[draft.targetField] ?? draft.targetField}</Badge>
        <span style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)' }}>
          {isKnowledge ? 'append' : draft.mode}
          {draft.proposalId ? ' · from an open proposal' : ' · ad-hoc'}
        </span>
        {applied && <Badge variant="success">applied ✓</Badge>}
      </div>
      {draft.rationale && (
        <div style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)' }}>{draft.rationale}</div>
      )}
      <textarea
        value={text}
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
        rows={Math.min(8, Math.max(2, text.split('\n').length))}
        disabled={applied}
        style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid var(--m-border, #d1d5db)', fontSize: 13, fontFamily: 'inherit', lineHeight: 1.5 }}
      />
      {!applied && (
        <details>
          <summary style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)', cursor: 'pointer' }}>
            Current value of {TARGET_LABEL[draft.targetField] ?? draft.targetField}
          </summary>
          <pre style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: '6px 0 0', padding: 8, borderRadius: 6, border: '1px solid var(--m-border, #e5e7eb)', maxHeight: 140, overflowY: 'auto', background: 'var(--m-bg-subtle, #f9fafb)' }}>
            {currentTail || <span style={{ color: 'var(--m-text-muted, #9ca3af)' }}>— empty —</span>}
          </pre>
        </details>
      )}
      {apply.isError && (
        <Banner variant="error">{(apply.error as Error)?.message ?? "Couldn't apply."}</Banner>
      )}
      {!applied ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button onClick={() => apply.mutate()} loading={apply.isPending} disabled={!text.trim()}>
            Approve &amp; apply
          </Button>
          <span style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)' }}>edit the text first if needed</span>
        </div>
      ) : (
        <div style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)' }}>
          Live for the bot now · revert from Quality → Recently applied
        </div>
      )}
    </div>
  )
}
